import type { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { NAVIGATION_FALLBACK_ICON } from '../shared/page-command-icons';

/** Result groups, rendered and ranked in this order. */
export type CommandPaletteSection =
    | 'Current Page'
    | 'Quick Actions'
    | 'Selected Record'
    | 'Items Grid'
    | 'Customer Grid'
    | 'Supplier Grid'
    | 'Global Actions'
    | 'Navigation';

export const COMMAND_PALETTE_SECTION_ORDER: readonly CommandPaletteSection[] = [
    'Selected Record',
    'Items Grid',
    'Customer Grid',
    'Supplier Grid',
    'Current Page',
    'Quick Actions',
    'Global Actions',
    'Navigation',
];

/** One palette row: a page to open or an action to run on the current page. */
export interface CommandPaletteEntry {
    id: string;
    /** Row label, e.g. "Sale - Billing" or "Save / Update". */
    title: string;
    section: CommandPaletteSection;
    /** Ancestor labels for pages ("Transactions > Sale"), page label for commands. */
    group: string;
    icon?: string;
    /** Shortcut label shown right-aligned, e.g. "F10". Display only. */
    hint?: string;
    /**
     * Digit shortcut for a Selected Record action, "1"–"9".
     * Assigned from the visible list when the palette renders. Pressing it runs the action.
     */
    shortcut?: string;
    /** Shown but not runnable, e.g. a selected-record action before a row is highlighted. */
    disabled?: boolean;
    /** Page this command opens. A Navigation row with the same URL is dropped as a duplicate. */
    route?: string;
    run: () => void;
}

/** Cap on navigation rows. Page commands are never capped; no page contributes near this many. */
export const COMMAND_PALETTE_NAVIGATION_LIMIT = 20;

/** Tighter navigation cap when the active page also contributes commands. */
export const COMMAND_PALETTE_NAVIGATION_LIMIT_WITH_PAGE = 8;

/**
 * Depth-first walk of the navigation tree.
 * A node becomes a result when resolveUrl returns a URL (truthy link, not external, not disabled).
 * Hidden nodes and their children are skipped. Duplicate URLs keep the first occurrence.
 */
export function flattenNavigationItems(
    nodes: readonly FuseNavigationItem[] | null | undefined,
    resolveUrl: (item: FuseNavigationItem) => string | null,
    openPage: (item: FuseNavigationItem) => void,
): CommandPaletteEntry[] {
    const results: CommandPaletteEntry[] = [];
    const seenUrls = new Set<string>();

    const walk = (items: readonly FuseNavigationItem[], ancestors: string[]): void => {
        for (const item of items) {
            if (!item || item.hidden?.(item)) {
                continue;
            }

            const title = (item.title ?? '').trim();
            const url = resolveUrl(item);
            if (url && !seenUrls.has(url)) {
                seenUrls.add(url);
                results.push({
                    id: url,
                    title: title || url,
                    section: 'Navigation',
                    group: ancestors.join(' > '),
                    icon: item.icon || NAVIGATION_FALLBACK_ICON,
                    run: () => openPage(item),
                });
            }

            if (item.children?.length) {
                walk(item.children, title ? [...ancestors, title] : ancestors);
            }
        }
    };

    if (nodes?.length) {
        walk(nodes, []);
    }
    return results;
}

function sectionRank(section: CommandPaletteSection): number {
    const index = COMMAND_PALETTE_SECTION_ORDER.indexOf(section);
    return index < 0 ? COMMAND_PALETTE_SECTION_ORDER.length : index;
}

/**
 * Case-insensitive substring match over page commands then navigation pages.
 * Sort: section order, then title-prefix over title-contains over group-contains,
 * then original order. Navigation is capped; page and app commands are not.
 * An empty query lists every page and app command plus the first navigation pages.
 * App commands are always present, so only page commands tighten the navigation cap.
 */
export function filterCommandPaletteEntries(
    pageCommands: readonly CommandPaletteEntry[],
    navigationEntries: readonly CommandPaletteEntry[],
    query: string,
    appCommands: readonly CommandPaletteEntry[] = [],
): CommandPaletteEntry[] {
    const navigationLimit = pageCommands.length
        ? COMMAND_PALETTE_NAVIGATION_LIMIT_WITH_PAGE
        : COMMAND_PALETTE_NAVIGATION_LIMIT;

    const appRoutes = new Set(appCommands.map((entry) => entry.route).filter(Boolean));
    const navigation = appRoutes.size
        ? navigationEntries.filter((entry) => !appRoutes.has(entry.id.split('?')[0]))
        : navigationEntries;

    const matchedPageCommands = matchEntries([...pageCommands, ...appCommands], query);
    const matchedNavigation = matchEntries(navigation, query).slice(0, navigationLimit);

    return [...matchedPageCommands, ...matchedNavigation];
}

function matchEntries(
    entries: readonly CommandPaletteEntry[],
    query: string,
): CommandPaletteEntry[] {
    const normalized = query.trim().toLowerCase();

    const matched: { entry: CommandPaletteEntry; rank: number; index: number }[] = [];
    for (let index = 0; index < entries.length; index++) {
        const entry = entries[index];
        if (!normalized) {
            matched.push({ entry, rank: 0, index });
            continue;
        }

        const title = entry.title.toLowerCase();
        const group = entry.group.toLowerCase();
        const hint = (entry.hint ?? '').toLowerCase();
        let rank = 0;
        if (title.startsWith(normalized)) {
            rank = 3;
        } else if (title.includes(normalized)) {
            rank = 2;
        } else if (hint.includes(normalized)) {
            rank = 2;
        } else if (group.includes(normalized)) {
            rank = 1;
        }
        if (rank > 0) {
            matched.push({ entry, rank, index });
        }
    }

    matched.sort(
        (a, b) =>
            sectionRank(a.entry.section) - sectionRank(b.entry.section) ||
            b.rank - a.rank ||
            a.index - b.index,
    );
    return matched.map((item) => item.entry);
}

/** Consecutive runs of one section, so the dialog can render a header per group. */
export interface CommandPaletteGroup {
    section: CommandPaletteSection;
    entries: CommandPaletteEntry[];
    /** Index of this group's first entry in the flat result list (arrow-key index). */
    startIndex: number;
}

export function groupCommandPaletteEntries(
    entries: readonly CommandPaletteEntry[],
): CommandPaletteGroup[] {
    const groups: CommandPaletteGroup[] = [];
    for (let index = 0; index < entries.length; index++) {
        const entry = entries[index];
        const current = groups[groups.length - 1];
        if (current && current.section === entry.section) {
            current.entries.push(entry);
            continue;
        }
        groups.push({ section: entry.section, entries: [entry], startIndex: index });
    }
    return groups;
}
